import React from 'react';
import {
  Box, Typography, IconButton, Tooltip, Container, Paper,
} from '@mui/material';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import DirectionsIcon from '@mui/icons-material/Directions';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { AddToCalendarButton } from 'add-to-calendar-button-react';
import { useTranslation } from 'react-i18next';
import Calendar from '../files/icons/calendar.svg';
import Townhall from '../files/icons/townhall.svg';
import Brunch from '../files/icons/brunch.svg';
import Toast from '../files/icons/toast.svg';

function WeddingDetails() {
  const { t } = useTranslation();

  const handleDirections = (address) => {
    window.open(`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}`);
  };

  const handleCopyAddress = (address) => {
    navigator.clipboard.writeText(address);
  };

  const weddingDate = new Date('2025-07-12T13:00:00Z');
  const formattedDate = weddingDate.toLocaleDateString('fr-FR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'Europe/Paris',
  });

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      minHeight: '80vh',
      backgroundColor: 'background.default',
      py: 8,
    }}
    >
      <Container maxWidth="lg">
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: { xs: 6, md: 8 },
          alignItems: 'flex-start',
        }}
        >
          {/* QUAND Section */}
          <Paper
            elevation={0}
            sx={{
              p: 4,
              borderRadius: 2,
              backgroundColor: 'background.paper',
              width: { xs: '100%', md: '300px' },
              flexShrink: 0,
            }}
          >
            <Typography
              variant="h5"
              sx={{
                color: 'terracota.main',
                mb: 3,
                textAlign: 'center',
                fontWeight: 'medium',
              }}
            >
              {t('wedding.when')}
            </Typography>
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
            }}
            >
              <img src={Calendar} alt="calendar" style={{ width: 48, height: 48 }} />
              <Typography
                variant="h6"
                sx={{
                  textAlign: 'center',
                  color: 'text.primary',
                }}
              >
                Le
                {' '}
                {formattedDate}
              </Typography>
            </Box>
          </Paper>

          {/* OÙ Section */}
          <Paper
            elevation={0}
            sx={{
              p: 4,
              borderRadius: 2,
              backgroundColor: 'background.paper',
              flexGrow: 1,
            }}
          >
            <Typography
              variant="h5"
              sx={{
                color: 'terracota.main',
                mb: 4,
                textAlign: 'center',
                fontWeight: 'medium',
              }}
            >
              {t('wedding.where')}
            </Typography>

            <Box
              sx={{
                maxWidth: 400,
                width: '100%',
                mx: 'auto',
                display: 'flex',
                flexDirection: 'column',
                gap: 4,
                alignItems: 'center',
              }}
            >
              {/* Cérémonie civile */}
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 3,
                p: 2,
                borderRadius: 1,
                width: '100%',
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
              >
                <img src={Townhall} alt="townhall" style={{ width: 48, height: 48, flexShrink: 0 }} />
                <Box>
                  <Typography variant="h6" sx={{ mb: 0.5 }}>{t('wedding.civil_ceremony.time')}</Typography>
                  <Typography variant="h6" sx={{ mb: 1 }}>{t('wedding.civil_ceremony.location')}</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      {t('wedding.civil_ceremony.address')}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title={t('wedding.civil_ceremony.copy_address')}>
                        <IconButton
                          size="small"
                          onClick={() => handleCopyAddress('Hent Reuniou, 29950 Gouesnach')}
                        >
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('wedding.civil_ceremony.directions')}>
                        <IconButton
                          size="small"
                          onClick={() => handleDirections('Hent Reuniou, 29950 Gouesnach')}
                        >
                          <DirectionsIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                <ArrowDownwardIcon sx={{ color: 'terracota.main' }} />
              </Box>

              {/* Cérémonie laïque */}
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 3,
                p: 2,
                borderRadius: 1,
                width: '100%',
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
              >
                <img src={Toast} alt="toast" style={{ width: 48, height: 48, flexShrink: 0 }} />
                <Box>
                  <Typography variant="h6" sx={{ mb: 0.5 }}>{t('wedding.secular_ceremony.time')}</Typography>
                  <Typography variant="h6" sx={{ mb: 1 }}>{t('wedding.secular_ceremony.location')}</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      {t('wedding.secular_ceremony.address')}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title={t('wedding.secular_ceremony.copy_address')}>
                        <IconButton
                          size="small"
                          onClick={() => handleCopyAddress('1, allée de Prad ar C\'Hras, 29000 Quimper')}
                        >
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('wedding.secular_ceremony.directions')}>
                        <IconButton
                          size="small"
                          onClick={() => handleDirections('1, allée de Prad ar C\'Hras, 29000 Quimper')}
                        >
                          <DirectionsIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                </Box>
              </Box>

              {/* Cocktail */}
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 3,
                mt: -4,
                p: 2,
                borderRadius: 1,
                width: '100%',
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
              >
                <Box sx={{ width: 48, height: 48 }} />
                <Typography variant="h6">{t('wedding.cocktail.time')}</Typography>
              </Box>

              {/* Diner */}
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 3,
                p: 2,
                mt: -4,
                borderRadius: 1,
                width: '100%',
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
              >
                <Box sx={{ width: 48, height: 48 }} />
                <Typography variant="h6">{t('wedding.dinner.time')}</Typography>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                <ArrowDownwardIcon sx={{ color: 'terracota.main' }} />
              </Box>

              {/* Brunch */}
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 3,
                p: 2,
                borderRadius: 1,
                width: '100%',
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
              >
                <img src={Brunch} alt="brunch" style={{ width: 48, height: 48, flexShrink: 0 }} />
                <Box>
                  <Typography variant="h6" sx={{ mb: 0.5 }}>{t('wedding.brunch.title')}</Typography>
                  <Typography variant="h6" sx={{ mb: 1 }}>{t('wedding.brunch.location')}</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      {t('wedding.brunch.address')}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title={t('wedding.brunch.copy_address')}>
                        <IconButton
                          size="small"
                          onClick={() => handleCopyAddress('7 Hent Kermao, 29950 Gouesnach')}
                        >
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('wedding.brunch.directions')}>
                        <IconButton
                          size="small"
                          onClick={() => handleDirections('7 Hent Kermao, 29950 Gouesnach')}
                        >
                          <DirectionsIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      fontSize: '0.7rem',
                      mt: 2,
                      display: 'block',
                    }}
                  >
                    {t('wedding.brunch.parking_info')}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Paper>
        </Box>

        <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          mt: 6,
        }}
        >
          <AddToCalendarButton
            name="Mariage Pauline & Pierre"
            startDate="2025-07-12"
            startTime="15:00"
            endTime="23:59"
            location="19 Route de Bénodet, 29950 Gouesnach"
            options={['Google', 'iCal', 'Outlook.com', 'Yahoo']}
            buttonStyle="text"
            size="small"
            label={t('wedding.calendar.add')}
          />
        </Box>
      </Container>
    </Box>
  );
}

export default WeddingDetails;
